import React from "react";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../requests/shared/helpers/DrawerFooterContainerSmall";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ViewQuiltOutlinedIcon from "@mui/icons-material/ViewQuiltOutlined";
import {
  DrawerSaveFooter,
} from "./configure/drawerInputs";

const TYPE_STYLES = {
  category: "bg-sky-100 text-sky-700",
  metric: "bg-emerald-100 text-emerald-700",
  table: "bg-amber-100 text-amber-700",
  agent: "bg-violet-100 text-violet-700",
};

// Layout templates: each is a full designed page. The preview renders the
// actual rows of blocks the apply produces, and "Use this layout" replaces
// the company's arrangement (categories and entries are always kept;
// existing categories the template doesn't mention drop to the unplaced
// strip).
function LogbookTemplateDrawer(props) {
  const selectedTemplate = (props.templates || []).find(
    (t) => t.id === props.selectedTemplateId
  );

  const countsLine = (template) => {
    const parts = [
      `${template.category_count} ${
        template.category_count === 1 ? "category" : "categories"
      }${
        template.new_category_count > 0
          ? ` (${template.new_category_count} new)`
          : ""
      }`,
    ];
    if (template.metric_count > 0) {
      parts.push(
        `${template.metric_count} ${template.metric_count === 1 ? "metric" : "metrics"}`
      );
    }
    if (template.table_count > 0) {
      parts.push(`${template.table_count} ${template.table_count === 1 ? "table" : "tables"}`);
    }
    if (template.agent_count > 0) {
      parts.push(
        `${template.agent_count} AI ${template.agent_count === 1 ? "agent" : "agents"}`
      );
    }
    return parts.join(" · ");
  };

  return (
    <Drawer anchor="right" open={props.open} onClose={props.onClose}>
      <div className="w-[1150px] max-w-[95vw] h-full bg-slate-50 relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader title="Logbook Templates" onClose={props.onClose} />
          <div className="w-full px-6">
            <div className="-mt-4 bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 flex items-start gap-2.5">
              <ViewQuiltOutlinedIcon
                style={{ fontSize: "18px", color: "#6d28d9", marginTop: "1px" }}
              />
              <div>
                <p className="text-sm font-semibold text-violet-900">
                  A finished page, not just categories.
                </p>
                <p className="text-xs text-violet-700 mt-0.5">
                  Using a template replaces your current arrangement with this
                  one. Categories and their entries are always kept: matching
                  categories are reused, missing ones are created, and anything
                  the template does not mention stays available below the
                  layout.
                </p>
              </div>
            </div>

            <div className="mt-5 flex gap-4 items-start">
              {/* Template list */}
              <div className="w-56 shrink-0 flex flex-col gap-2">
                {(props.templates || []).map((template) => {
                  const isSelected = template.id === props.selectedTemplateId;
                  return (
                    <div
                      key={template.id}
                      className={`rounded-xl border px-3 py-2.5 cursor-pointer ${
                        isSelected
                          ? "border-violet-500 bg-white"
                          : "border-slate-200 bg-white hover:border-slate-400"
                      }`}
                      onClick={() => props.setSelectedTemplateId(template.id)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-slate-900">
                          {template.name}
                        </p>
                        {isSelected && (
                          <CheckCircleIcon
                            style={{ fontSize: "16px", color: "#6d28d9" }}
                          />
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {countsLine(template)}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Mini-canvas preview */}
              <div className="flex-1 min-w-0">
                {selectedTemplate ? (
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {selectedTemplate.name}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {selectedTemplate.description}
                    </p>
                    <div className="mt-3 flex flex-col gap-1.5">
                      {(selectedTemplate.rows || []).map((row, rowIndex) => {
                        const isSectionRow =
                          row.blocks.length === 1 &&
                          row.blocks[0].type === "section";
                        if (isSectionRow) {
                          return (
                            <p
                              key={rowIndex}
                              className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2"
                            >
                              {row.blocks[0].label}
                            </p>
                          );
                        }
                        return (
                          <div key={rowIndex} className="flex gap-1.5">
                            {row.blocks.map((block, blockIndex) => (
                              <div
                                key={blockIndex}
                                className="flex-1 min-w-0 flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2 py-1.5"
                              >
                                <p className="flex-1 min-w-0 text-xs font-medium text-slate-800 truncate">
                                  {block.label}
                                </p>
                                <span
                                  className={`text-[9px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 ${
                                    TYPE_STYLES[block.type] ||
                                    "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {block.type}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    {selectedTemplate.rows?.some((row) =>
                      row.blocks.some((b) => b.type === "table")
                    ) && (
                      <p className="text-[11px] text-slate-400 mt-2">
                        Table rows:{" "}
                        {selectedTemplate.rows
                          .flatMap((row) => row.blocks)
                          .filter((b) => b.type === "table")
                          .flatMap((b) => b.config?.row_labels || [])
                          .join(", ")}
                      </p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-2">
                      Metric targets are not set by templates; add flag targets
                      from each block's settings after applying.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Select a template</p>
                )}
              </div>
            </div>
          </div>
          <DrawerFooterContainerSmall>
            <DrawerSaveFooter
              onSave={props.applyTemplate}
              onClose={props.onClose}
              saving={props.isApplyingTemplate}
              saveText="Use this layout"
              horizontal
            />
          </DrawerFooterContainerSmall>
          <div className="h-[200px]"></div>
        </div>
      </div>
    </Drawer>
  );
}

export default LogbookTemplateDrawer;
